import { Callback, Dict, Query, RequestOptions } from 'mixpanel-browser';
import * as React from 'react';

import { getMixpanel } from '../../utils/getMixpanel';

/**
 * @see https://github.com/mixpanel/mixpanel-js/blob/master/doc/readme.io/javascript-full-api-reference.md#mixpaneltrack
 */
export type MixpanelTrackParamsType = {
  eventName: string;
  properties?: Dict;
  optionsOrCallback?: Callback | RequestOptions;
  callback?: Callback;
};

/**
 * @see https://github.com/mixpanel/mixpanel-js/blob/master/doc/readme.io/javascript-full-api-reference.md#mixpaneltrack_forms
 */
export type MixpanelTrackFormsParamsType = {
  query: Query;
  eventName: string;
  properties?: Dict | (() => void);
};

/**
 * @see https://github.com/mixpanel/mixpanel-js/blob/master/doc/readme.io/javascript-full-api-reference.md#mixpaneltrack_links
 */
export type MixpanelTrackLinkParamsType = {
  query: Query;
  eventName: string;
  properties?: Dict | (() => void);
};

/**
 * @see https://github.com/mixpanel/mixpanel-js/blob/master/doc/readme.io/javascript-full-api-reference.md#mixpaneltrack_with_groups
 */
export type MixpanelTrackWithGroupsParamsType = {
  eventName: string;
  properties: Dict;
  groups: Dict;
  callback?: Callback;
};

/**
 * @see https://docs.mixpanel.com/docs/tracking/reference/javascript#tracking-page-views
 */
export type MixpanelTrackPageViewParamsType = {
  properties?: Dict;
};

export type MixpanelTrackEventFnType = (
  options: MixpanelTrackParamsType,
) => void;
export type MixpanelTrackFormsEventFnType = (
  options: MixpanelTrackFormsParamsType,
) => void;
export type MixpanelTrackLinksFnType = (
  options: MixpanelTrackLinkParamsType,
) => void;
export type MixpanelTrackWithGroupsFnType = (
  options: MixpanelTrackWithGroupsParamsType,
) => void;
export type MixpanelTrackPageviewFnType = (
  options: MixpanelTrackPageViewParamsType,
) => void;

export type UseTrackingHookType = () => {
  track: MixpanelTrackEventFnType;
  trackForms: MixpanelTrackFormsEventFnType;
  trackLinks: MixpanelTrackLinksFnType;
  trackWithGroups: MixpanelTrackWithGroupsFnType;
  trackPageview: MixpanelTrackPageviewFnType;
};

export const useTracking: UseTrackingHookType = () => {
  const mixpanel = getMixpanel();

  const track: MixpanelTrackEventFnType = (trackingOptions) => {
    const { eventName, properties, optionsOrCallback, callback } =
      trackingOptions;
    if (mixpanel?.track) {
      mixpanel.track(eventName, properties, optionsOrCallback, callback);
    }
  };

  const trackForms: MixpanelTrackFormsEventFnType = (trackingOptions) => {
    const { query, eventName, properties } = trackingOptions;
    if (mixpanel?.track_forms) {
      mixpanel.track_forms(query, eventName, properties);
    }
  };

  const trackLinks: MixpanelTrackLinksFnType = (trackingOptions) => {
    const { query, eventName, properties } = trackingOptions;
    if (mixpanel?.track_links) {
      mixpanel.track_links(query, eventName, properties);
    }
  };

  const trackWithGroups: MixpanelTrackWithGroupsFnType = (trackingOptions) => {
    const { eventName, properties, groups, callback } = trackingOptions;
    if (mixpanel?.track_with_groups) {
      mixpanel.track_with_groups(eventName, properties, groups, callback);
    }
  };

  const trackPageview: MixpanelTrackPageviewFnType = (trackingOptions) => {
    const { properties } = trackingOptions;
    if (mixpanel?.track_pageview) {
      mixpanel.track_pageview(properties);
    }
  };

  return React.useMemo(() => {
    return {
      track,
      trackForms,
      trackLinks,
      trackWithGroups,
      trackPageview,
    };
    /* eslint-disable react-hooks/exhaustive-deps */
  }, [!!mixpanel]);
};
